import { ConsignmentApiModel, Dimensions, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { distributeIntoParcels, linesToContents } from '~/apps/smartsend/lib/parcels/allocate';
import { DistributeOptions } from '~/apps/smartsend/lib/parcels/types';
import { canPadAcrossParcels } from '~/apps/smartsend/lib/parcels/bounds';

const consignmentOptions = (itemId: string, dimensions: Dimensions): Omit<DistributeOptions, 'overflow'> => ({
  granularity: 'units',
  // Every parcel of a consignment belongs to the one owning item.
  parcelId: () => itemId,
  dimensions,
  canAdd: true,
  splitWeight: true
});

const unitCount = (rows: { Quantity: number }[]) => rows.reduce((total, row) => total + (row.Quantity || 1), 0);

/**
 * Builds a multi-parcel consignment for a single item.
 *
 * A single order line PADS out to the requested parcel count, so one physical item can be sent in
 * several boxes even though only one unit was ordered - the surplus parcels get a quantity-1 copy of
 * the line for the user to redescribe. With several lines we CLAMP at the unit count instead,
 * because there is no way to know which line a surplus parcel would be for.
 *
 * Both contracts predate SC-2108 and are preserved exactly; see allocate.ts for the detail.
 */
export function GenerateConsignmentFromItem(
  item: SmartSendItemApiModel,
  dimensions: Dimensions,
  requestedParcels: number
): ConsignmentApiModel {
  const contents = linesToContents(item.MarketplaceOrderLines);
  // Shared with the parcel-count picker via consignmentParcelBound, so the range offered and the range
  // buildable cannot drift apart - they did, and every option above the piece count did nothing.
  const canPad = canPadAcrossParcels(item.MarketplaceOrderLines) && unitCount(contents) < requestedParcels;

  return distributeIntoParcels(contents, requestedParcels, {
    ...consignmentOptions(item.Id, dimensions),
    overflow: canPad ? 'pad-with-copies' : 'clamp'
  });
}

/**
 * Re-splits an already-customised consignment across a new parcel count, preserving per-row edits
 * (Description/Sku/TariffCode/CountryOfManufacture overrides and manually added rows) instead of
 * rebuilding from the order lines and throwing them away.
 *
 * Always clamps: redistribution works from existing rows, so there is nothing to pad from.
 */
export function RedistributeConsignmentParcels(
  itemId: string,
  existing: ConsignmentApiModel,
  dimensions: Dimensions,
  requestedParcels: number
): ConsignmentApiModel {
  const contents = existing.Parcels.flatMap((parcel) => parcel.Contents);

  if (contents.length === 0) {
    // Returned by identity on purpose - callers pass the result straight to onUpdate.
    return existing;
  }

  return distributeIntoParcels(contents, requestedParcels, {
    ...consignmentOptions(itemId, dimensions),
    overflow: 'clamp'
  });
}
